import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Stack,
    IconButton,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    AccessTime as ClockIcon,
    TrendingUp as AnalyticsIcon,
    Engineering as TechIcon,
    ErrorOutline as IssueIcon,
    CheckCircle as SuccessIcon,
    Timeline as TimelineIcon,
    PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import serviceTicketService from '../../services/serviceTicketService';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const ServiceAnalytics = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [dateRange, setDateRange] = useState('30_days');

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const end = new Date();
            const start = new Date();
            if (dateRange === '7_days') start.setDate(end.getDate() - 7);
            else if (dateRange === '30_days') start.setDate(end.getDate() - 30);
            else if (dateRange === '90_days') start.setDate(end.getDate() - 90);

            const response = await serviceTicketService.getAnalytics(start.toISOString(), end.toISOString());
            if (response.success) setData(response.data);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusChartData = useMemo(() => {
        if (!data?.statusStats) return null;
        const colors = { 'OPEN': '#3B82F6', 'ASSIGNED': '#06B6D4', 'IN_PROGRESS': '#F97316', 'WAITING_SPARES': '#A855F7', 'RESOLVED': '#22C55E', 'CLOSED': '#6B7280', 'CANCELLED': '#EF4444' };
        return {
            labels: data.statusStats.map(s => s.status.replace('_', ' ')),
            datasets: [{
                data: data.statusStats.map(s => s.count),
                backgroundColor: data.statusStats.map(s => colors[s.status] || '#9CA3AF'),
                borderWidth: 0,
                hoverOffset: 20
            }]
        };
    }, [data]);

    const technicianChartData = useMemo(() => {
        if (!data?.techPerformance) return null;
        return {
            labels: data.techPerformance.map(t => t.name.split(' ')[0]),
            datasets: [
                { label: 'Tickets', data: data.techPerformance.map(t => t.tickets_handled), backgroundColor: '#3B82F6', borderRadius: 8 },
                { label: 'Labor Hours', data: data.techPerformance.map(t => parseFloat(t.total_hours)), backgroundColor: '#A855F7', borderRadius: 8 }
            ]
        };
    }, [data]);

    return (
        <Box p={3} className="animate-fade-in">
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <IconButton onClick={() => navigate('/service')} sx={{ bgcolor: 'white', boxShadow: 'var(--shadow-sm)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <ToggleButtonGroup
                    value={dateRange}
                    exclusive
                    onChange={(e, val) => val && setDateRange(val)}
                    size="small"
                    sx={{ bgcolor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', p: 0.5 }}
                >
                    <ToggleButton value="7_days" sx={{ px: 2, borderRadius: '8px !important', border: 'none', fontWeight: 700 }}>7D</ToggleButton>
                    <ToggleButton value="30_days" sx={{ px: 2, borderRadius: '8px !important', border: 'none', fontWeight: 700 }}>30D</ToggleButton>
                    <ToggleButton value="90_days" sx={{ px: 2, borderRadius: '8px !important', border: 'none', fontWeight: 700 }}>90D</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* KPI Cards */}
            <Grid container spacing={3} mb={5}>
                {[
                    { label: 'Resolution SLA', value: `${data?.avgMetrics?.avg_resolution_hours ? Math.round(data.avgMetrics.avg_resolution_hours) : 0}h`, sub: 'Avg per ticket', icon: <ClockIcon />, color: '#3b82f6' },
                    { label: 'Labor Intensity', value: `${data?.avgMetrics?.avg_labor_hours ? parseFloat(data.avgMetrics.avg_labor_hours).toFixed(1) : 0}h`, sub: 'Hours/ticket', icon: <TechIcon />, color: '#8b5cf6' },
                    { label: 'Completion Rate', value: `${(() => { const total = data?.statusStats?.reduce((a, c) => a + c.count, 0) || 0; const resolved = data?.statusStats?.find(s => s.status === 'RESOLVED' || s.status === 'CLOSED')?.count || 0; return total ? Math.round((resolved / total) * 100) : 0; })()}%`, sub: 'Overall efficiency', icon: <SuccessIcon />, color: '#10b981' },
                    { label: 'Primary Issue', value: data?.topIssues?.[0]?.issue_type.replace('_', ' ') || 'N/A', sub: `${data?.topIssues?.[0]?.count || 0} occurrences`, icon: <IssueIcon />, color: '#f59e0b' }
                ].map((stat, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, borderRadius: '12px' }}>{stat.icon}</Avatar>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{loading ? <Skeleton width={40} /> : stat.value}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>{stat.label}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontSize: '11px', fontWeight: 600 }}>{stat.sub}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardHeader title="Ticket Distribution" titleTypographyProps={{ variant: 'h6', fontWeight: 800 }} subheader="By current status" />
                        <Divider />
                        <CardContent sx={{ height: 350, display: 'flex', alignItems: 'center' }}>
                            {loading ? <Skeleton variant="circular" width={250} height={250} /> : statusChartData ? (
                                <Doughnut data={statusChartData} options={{ plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20, font: { weight: 'bold' } } } }, maintainAspectRatio: false, cutout: '75%' }} />
                            ) : <Typography variant="body2" color="text.disabled">No data</Typography>}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={8}>
                    <Card sx={{ height: '100%' }}>
                        <CardHeader title="Technical Workforce Analysis" titleTypographyProps={{ variant: 'h6', fontWeight: 800 }} subheader="Resource allocation vs volume" />
                        <Divider />
                        <CardContent sx={{ height: 350 }}>
                            {loading ? <Skeleton variant="rectangular" height="100%" /> : technicianChartData ? (
                                <Bar data={technicianChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top', align: 'end' } }, scales: { x: { grid: { display: false } }, y: { beginAtZero: true } } }} />
                            ) : <Typography variant="body2" color="text.disabled">No data</Typography>}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardHeader title="Root Cause Frequency" titleTypographyProps={{ variant: 'h6', fontWeight: 800 }} subheader="Identifying recurring failure points" />
                        <Divider />
                        <CardContent sx={{ height: 300 }}>
                            {loading ? <Skeleton variant="rectangular" height="100%" /> : data?.topIssues ? (
                                <Bar
                                    data={{
                                        labels: data.topIssues.map(i => i.issue_type.replace('_', ' ')),
                                        datasets: [{ label: 'Occurrences', data: data.topIssues.map(i => i.count), backgroundColor: 'rgba(249, 115, 22, 0.2)', borderColor: '#F97316', borderWidth: 2, borderRadius: 8 }]
                                    }}
                                    options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false }, beginAtZero: true } } }}
                                />
                            ) : <Typography variant="body2" color="text.disabled">No data</Typography>}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

const CardHeader = ({ title, subheader }) => (
    <Box p={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{subheader}</Typography>
    </Box>
);

export default ServiceAnalytics;
